import { useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { Link } from "iconsax-reactjs";
import CategorySearch from "./CategorySearch";
import TinyMCE from "./TinyMCE";
import InputFiel from "../../commons/InputFiel";
import type { Category } from "../../../services/webServices/category/types";
import { postCategory } from "../../../services/webServices/category/post";
import { patchCategory } from "../../../services/webServices/category/patch";

export interface AddCategoryFormValues {
  id?: number;
  name: string;
  parent?: {
    id: number;
    title: string;
    slug: string;
    depth: number;
    breadcrump?: string;
  } | null;
  link: string;
  description: string;
}

interface AddCategoryFormProps {
  categories: Category[];
  onCancel: () => void;
  onSaved: () => void;
  initialValues?: AddCategoryFormValues;
}

export default function AddCategoryForm({ categories, onCancel, onSaved, initialValues }: AddCategoryFormProps) {

  const isEditing = Boolean(initialValues?.id);

  const form = useForm<AddCategoryFormValues>({
    defaultValues: initialValues || {
      name: "",
      parent: null,
      link: "",
      description: "",
    },
  });

  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { isSubmitting },
  } = form;

  useEffect(() => {
    if (initialValues) reset(initialValues);
  }, [initialValues, reset]);

  const onSubmit: SubmitHandler<AddCategoryFormValues> = async (data) => {
    if (!data.name.trim() || !data.link.trim() || !data.description.trim()) return;

    const body: any = {
      title: data.name.trim(),
      slug: data.link.trim(),
      description: data.description.trim(),
    };

    if (!isEditing) {
      body.parent = data.parent?.id || null;
    }

    const success = isEditing
      ? await patchCategory({ id: initialValues!.id!, body })
      : await postCategory({ body });

    if (!success) return;

    onSaved();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full px-4">
      <div className="grid grid-cols-1 gap-4">
        <div className="flex flex-col gap-2">
          <label className="text-base text-gray-500 font-normal">
            نام دسته <span className="text-primaryVar">*</span>
          </label>
          <InputFiel
            as="input"
            size="lg"
            value={watch("name") || ""}
            placeholder="نام دسته بندی"
            maxLength="unlimited"
            disabled={isSubmitting}
            onChange={(e) => setValue("name", e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-base text-gray-500 font-normal">
            انتخاب دسته پدر
          </label>
          <CategorySearch
            disabled={isEditing}
            initialValue={initialValues?.parent || null}
            onSelect={(title, id, breadcrumb) => {
              setValue("parent", {
                id: Number(id),
                title,
                slug: "",
                depth: 0,
                breadcrump: breadcrumb,
              });
            }}
            onClear={() => setValue("parent", null)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-base text-gray-500 font-normal">
            لینک صفحه <span className="text-primaryVar">*</span>
          </label>
          <InputFiel
            as="input"
            size="lg"
            value={watch("link") || ""}
            placeholder="لینک"
            maxLength="unlimited"
            disabled={isSubmitting}
            onChange={(e) => setValue("link", e.target.value)}
          />
          <div className="flex items-start gap-1 text-xs" dir="ltr">
            <Link variant="Outline" size={16} color="#6B7280" />
            <span className="text-gray-500">
              https://danajou.com/category/
              <span className="text-infoVar">{watch("link")}</span>
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-2" dir="rtl">
          <label className="text-sm text-gray-600">توضیحات</label>
          <div className="border rounded-lg overflow-hidden">
            <TinyMCE
              defaultValue={watch("description") || ""}
              onChange={(value) => setValue("description", value)}
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 mt-6">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="text-sm px-4 py-2 text-primaryVar rounded-lg border border-primaryVar hover:bg-primaryVar-50 disabled:opacity-50"
        >
          انصراف
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="bg-primaryVar hover:bg-darkPrimary text-white text-sm px-5 py-2 rounded-lg disabled:opacity-50"
        >
          ذخیره
        </button>
      </div>
    </form>
  );
};